import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { onYouTubeAccountVerificationCheck } from 'redux/modules/mixpanel';
import { YouTubeAccountVerificationCheckSource } from 'redux/modules/mixpanel/types';
import { showError } from 'redux/modules/notification';
import { validateYoutubeAccountElegibility } from 'redux/modules/social';
import { Dispatch } from 'redux/types';

interface UseVerifyYouTubeAccountConfig {
  source: YouTubeAccountVerificationCheckSource;
  onAccountVerified?: () => void;
  onAccountDisallowed?: () => void;
  onAccountEligible?: () => void;
  onAccountNoChannel?: () => void;
  onAccountVerificationFailure?: () => void;
}

interface UseVerifyYouTubeAccountResult {
  isVerifying: boolean;
  onVerifyAccount: () => Promise<void>;
}

export default function useVerifyYouTubeAccount({
  source,
  onAccountVerified,
  onAccountDisallowed,
  onAccountEligible,
  onAccountNoChannel,
  onAccountVerificationFailure,
}: UseVerifyYouTubeAccountConfig): UseVerifyYouTubeAccountResult {
  const [isVerifying, setIsVerifying] = useState(false);

  const dispatch = useDispatch<Dispatch>();

  const handleAccountDisallowed = useCallback(() => {
    onAccountDisallowed?.();
    dispatch(
      onYouTubeAccountVerificationCheck({
        source,
        status: 'Disallowed',
      }),
    );
  }, [dispatch, onAccountDisallowed, source]);

  const handleAccountEligible = useCallback(() => {
    onAccountEligible?.();
    dispatch(onYouTubeAccountVerificationCheck({ source, status: 'Eligible' }));
  }, [dispatch, onAccountEligible, source]);

  const handleAccountNoChannel = useCallback(() => {
    onAccountNoChannel?.();
    dispatch(
      onYouTubeAccountVerificationCheck({
        source,
        status: 'ChannelNeeded',
      }),
    );
  }, [dispatch, onAccountNoChannel, source]);

  const handleAccountVerificationFailure = useCallback(() => {
    dispatch(
      showError({
        message: 'YouTube account verification failed',
        code: 'IN019',
      }),
    );
    onAccountVerificationFailure?.();
  }, [dispatch, onAccountVerificationFailure]);

  const handleVerifyAccount = useCallback(async () => {
    setIsVerifying(true);

    try {
      const longUploadsStatus = await dispatch(
        validateYoutubeAccountElegibility(),
      );

      // The action to take when verifying the account is defined by the longUploadsStatus
      // value obtained from the channel request. Only when allowed the success
      // callback will be invoked.
      switch (longUploadsStatus) {
        case 'allowed':
          onAccountVerified?.();
          break;

        case 'disallowed':
          handleAccountDisallowed();
          break;

        case 'eligible':
          handleAccountEligible();
          break;

        default:
          // As defined by the BE when the channel validation returns no status,
          // it can be assumed that no channel is created for the account.
          handleAccountNoChannel();
          break;
      }
    } catch {
      handleAccountVerificationFailure();
    } finally {
      setIsVerifying(false);
    }
  }, [
    dispatch,
    handleAccountDisallowed,
    handleAccountEligible,
    handleAccountNoChannel,
    handleAccountVerificationFailure,
    onAccountVerified,
  ]);

  return {
    isVerifying,
    onVerifyAccount: handleVerifyAccount,
  };
}
